import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { applyColor }from '~src/style/ColorService';

import { Formatters,Messages } from '../../codegen/Localize';
import { cssClass, Style } from '../../style';
import _ = require('underscore');
import DesignTokens from '@tableau/design-tokens';
import { style } from 'typestyle';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

const borderRadiusValue: string = '4px';

export type DraftBannerAction = {
  label: string;
  /** Legacy raster icon path (e.g. imgPath('pencil_14px_small.svg')). Provide this OR `icon`. */
  iconPath?: string;
  /** Inline icon element (e.g. <TrashBaseIcon size={14} aria-hidden={true}/>). Takes precedence over `iconPath`. */
  icon?: React.ReactNode;
  handleAction: () => void;
  testId: string;
}

export type DraftBannerProps = {
  readonly lastEditDate: string;
  readonly contentTypeName: string;
  readonly draftBannerActions: DraftBannerAction[];
};

const DraftBannerWrapperClass = cssClass('DraftBannerWrapperClass', '', {
  display: 'inline-flex',
  height: '44px',
  backgroundColor: applyColor(DesignTokens.color.component.background.neutral.default.active, '#ebebeb'),
  alignItems: 'center',
  borderRadius: borderRadiusValue,
});

const DraftStatusWrapperClass = cssClass('DraftStatusWrapper', '', {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: applyColor(DesignTokens.color.component.background.header.inverse.info, CommonStyles.Colors.BadgeBlue),
  height: '100%',
  borderRadius: `${borderRadiusValue} 0 0 ${borderRadiusValue}`
});

const DraftStatusTextClass = cssClass('DraftStatusText', '', {
  fontWeight: 'bold',
  fontSize: '14px',
  color: applyColor(DesignTokens.color.font.inverse.default, '#fafafa'),
  padding: `0 ${TabStyles.Spacing.LargeSpace}px`,
});

const DateWrapperClass = cssClass('DateWrapperClass', '', {
  paddingLeft: TabStyles.Spacing.LargeSpace,
  paddingRight: '50px',
});

const ButtonsWrapperClass = cssClass('ButtonsWrapperClass', '', {
  display: 'flex',
});

const ButtonClass = cssClass('ButtonWrapperClass', '', {
  paddingRight: '32px',
});

const ButtonLabelClass = cssClass('ButtonLabelClass', '', {
  marginLeft: TabStyles.Spacing.DefaultSpace,
});

const buttonHoverStyle: Style = {
  $nest: {
    '&:hover:enabled': {
      textDecoration: 'underline',
      cursor: 'pointer'
    },
  },
};

const InlineButtonStyles: React.CSSProperties = {
  padding: `${TabStyles.Spacing.SmallSpace}px ${TabStyles.Spacing.DefaultSpace}px`,
  display: 'inline-flex',
  alignItems: 'center',
};

function renderButtons(draftBannerActions: DraftBannerAction[]): JSX.Element[] {
return _.map(draftBannerActions, (draftBannerAction: DraftBannerAction) => {
  /* eslint-disable jsx-a11y/alt-text */
  return (
    <div className={ButtonClass} key={`${draftBannerAction.testId}-key`}>
      <ButtonWidget
        className={style(buttonHoverStyle)}
        buttonType={ButtonType.LowEmphasis}
        testId={`draft-banner-${draftBannerAction.testId}`}
        handleClick={draftBannerAction.handleAction}
        title={draftBannerAction.label}
        style={InlineButtonStyles}
      >
        {draftBannerAction.icon
          ? draftBannerAction.icon
          : <img data-tb-test-id={'draft-banner-action-button-icon'} src={draftBannerAction.iconPath}/>}
        <span data-tb-test-id={'draft-banner-action-button-label'} className={ButtonLabelClass}>
          {draftBannerAction.label}
        </span>
      </ButtonWidget>
    </div>
    );
  });
}

export function DraftBanner(props: DraftBannerProps): JSX.Element {
  return (
    <div data-tb-test-id={'draft-banner'} className={DraftBannerWrapperClass} >
      <div className={DraftStatusWrapperClass}>
        <span className={DraftStatusTextClass}>{Messages.draftBannerHeader()}</span>
      </div>
      <div className={DateWrapperClass}>
        <span data-tb-test-id={'draft-banner-last-edited-message'}>
          {Messages.lastEdited({CONTENT_TYPE: props.contentTypeName, RELATIVE_DATE: Formatters.formatLongBestFitRelativeTime(new Date(props.lastEditDate))})}
        </span>
      </div>
      <div className={ButtonsWrapperClass}>
        {renderButtons(props.draftBannerActions)}
      </div>
    </div>
  );
}
